import { Context } from "urql";
import { publicConfig } from "@lululemon/ecom-core";

export const bazaarvoiceMutation = async ({ client, mutation, variables }) => {
  Context.url = `${publicConfig?.apiHost}/cne/graphql`;

  const { data, error } = await client
    .mutation(mutation, variables, Context)
    .toPromise();

  if (error) {
    throw error;
  }

  return data;
};

export const bazaarvoiceQuery = async ({ client, query, variables }) => {
  Context.url = `${publicConfig?.apiHost}/cne/graphql`;

  const { data, error: queryError } = await client
    .query(query, variables, Context)
    .toPromise();

  const error = queryError || data?.getReviewComments?.errors?.[0]?.message;
  if (error) {
    throw error;
  }

  return data;
};

const postContentPositiveFeedbackMutation = `
  mutation Mutation($locale: String!, $contentId: String!, $contentType: String!, $user: String!) {
    postContentFeedback(locale: $locale, contentId: $contentId, contentType: $contentType, user: $user) {
      errors {
        Message
        Code
      }
      feedback {
        authorId
        contentId
        contentType
        isPositiveFeedback
        feedbackType
      }
      hasErrors
    }
  }
`;

const getReviewsQuery = `query GetReviews($locale: String!, $productNameId: String!, $search: String, $offset: Int, $rating: [Int], $filters: [String], $sort: String) {
  getReviews(locale: $locale, productNameId: $productNameId, search: $search, offset: $offset, rating: $rating, filters: $filters, sort: $sort) {
    results {
      title
      reviewText
      rating
      badge
      comments {
        contributor {
          isStaff
          nickName
        }
        id
        likes
        submissionTime
        text
        reviewId
      }
      contributor {
        isStaff
        isVerifiedBuyer
        nickName
      }
      fitInformation {
        fits
        height
        sizePurchased
        weight
        whatIsYourUsualSize
      }
      id
      incentivizedReviewLabel
      likes
      luluResponse {
        nickName
        responseText
        submissionTime
      }
      photos {
        caption
        thumbnail
        id
        url
      }
      submissionTime
      totalComments
    }
    totalResults
    errors {
      message
      code
    }
    hasAdditionalReviews
    hasErrors
    limit
    offset
    stats {
      averageOverallRating
      features {
        averageRating
        featureId
      }
      fitsRating {
        range
        label
        rating
      }
      ratingDistribution {
        numberOfResults
        ratingValue
      }
      totalReviewCount
    }
  }
}`;

const getReviewCommentsQuery = `
  query Query($reviewId: String!, $offset: Int, $limit: Int) {
    getReviewComments(reviewId: $reviewId, offset: $offset, limit: $limit) {
      hasAdditionalComments
      hasErrors
      errors {
        code
        message
      }
      limit
      offset
      results {
        contributor {
          isStaff
          nickName
        }
        id,
        likes,
        submissionTime,
        text,
      }
      totalResults
    }
  }
`;

const getReviewFormQuery = `
  query GetReviewForm($productNameId: String!, $user: String) {
    getReviewForm(productNameId: $productNameId, user: $user) {
      fields {
        id
        label
        required
        value
        type
        ... on SelectInput {
          id
          label
          options {
            label
            value
            selected
          }
          required
          type
          value
        }
        ... on TextInput {
          default
          id
          label
          maxLength
          minLength
          required
          type
          value
        }
        ... on BooleanInput {
          default
          id
          label
          required
          type
          value
        }
        ... on IntegerInput {
          default
          id
          label
          required
          type
          value
        }
        ... on TextAreaInput {
          default
          id
          label
          minLength
          maxLength
          required
          type
          value
        }
      }
      errors {
        code
        message
      }
      hasErrors
    }
  }
`;

const authUserMutation = `
mutation AuthenticateUser($authtoken: String!) {
  authenticateUser(authtoken: $authtoken) {
    Authentication {
      User
    }
    Errors {
      Code
      Message
    }
    HasErrors
  }
}
`;

export const postAuthUser = async ({ authtoken, client }) => {
  return bazaarvoiceMutation({
    client,
    mutation: authUserMutation,
    variables: {
      authtoken,
    },
  });
};

export const postContentPositiveFeedback = async ({
  client,
  contentId,
  contentType,
  locale,
  user,
}) => {
  return bazaarvoiceMutation({
    client,
    mutation: postContentPositiveFeedbackMutation,
    variables: {
      contentId,
      contentType,
      locale,
      user,
    },
  });
};

export const getReviewsGQL = async ({
  client,
  locale,
  productNameId,
  offset,
  search,
  rating,
  filters,
  sort,
}) => {
  return bazaarvoiceQuery({
    client,
    query: getReviewsQuery,
    variables: {
      client,
      locale,
      productNameId,
      offset,
      search,
      rating,
      filters,
      sort,
    },
  });
};

export const getReviewComments = async ({
  client,
  reviewId,
  offset = 0,
  limit = 8,
}) => {
  return bazaarvoiceQuery({
    client,
    query: getReviewCommentsQuery,
    variables: {
      reviewId,
      offset,
      limit,
    },
  });
};

const postReviewMutation = `
  mutation PostReview(
    $activity: String,
    $allowGiveaway: Boolean,
    $bodyType: String,
    $callbackUrl: String!,
    $deviceFingerprint: String!,
    $email: String!,
    $isIncentivizedReview: Boolean!,
    $isSweatCollective: Boolean,
    $locale: String!,
    $nickname: String!,
    $photos: [[String]],
    $productNameId: String!,
    $rating: String!,
    $ratingFit: String,
    $sendEmailAlertWhenCommented: Boolean,
    $sendEmailAlertWhenPublished: Boolean,
    $text: String!,
    $title: String!,
    $whatIsYourUsualSize: String,
    $whatSizeDidYouPurchase: String
  ) {
    postReview(
      allowGiveaway: $allowGiveaway,
      callbackUrl: $callbackUrl,
      deviceFingerprint: $deviceFingerprint,
      email: $email,
      isIncentivizedReview: $isIncentivizedReview,
      isSweatCollective: $isSweatCollective,
      locale: $locale,
      nickname: $nickname,
      productNameId: $productNameId,
      rating: $rating,
      text: $text,
      title: $title,
      sendEmailAlertWhenCommented: $sendEmailAlertWhenCommented,
      sendEmailAlertWhenPublished: $sendEmailAlertWhenPublished,
      activity: $activity,
      bodyType: $bodyType,
      photos: $photos,
      ratingFit: $ratingFit,
      whatIsYourUsualSize: $whatIsYourUsualSize,
      whatSizeDidYouPurchase: $whatSizeDidYouPurchase
    ) {
      AuthorSubmissionToken
      Errors {
        Message
        Code
      }
      FormErrors {
        FieldErrors {
          contextdatavalue_Activity {
            Field
            Message
            Code
          }
          additionalfield_WhatSizeDidYouPurchase {
            Field
            Message
            Code
          }
          additionalfield_WhatIsYourUsualSize {
            Field
            Message
            Code
          }
          rating_Fit_22 {
            Field
            Message
            Code
          }
          contextdatavalue_IncentivizedReview {
            Field
            Message
            Code
          }
          usernickname {
            Field
            Message
            Code
          }
          useremail {
            Field
            Message
            Code
          }
          title {
            Field
            Message
            Code
          }
          reviewtext {
            Field
            Message
            Code
          }
          sendemailalertwhenpublished {
            Field
            Message
            Code
          }
          agreedtotermsandconditions {
            Field
            Message
            Code
          }
          isrecommended {
            Field
            Message
            Code
          }
          isratingsonly {
            Field
            Message
            Code
          }
          sendemailalertwhencommented {
            Field
            Message
            Code
          }
          rating {
            Field
            Message
            Code
          }
          photourl_1 {
            Field
            Message
            Code
          }
          photocaption_1 {
            Field
            Message
            Code
          }
          photourl_2 {
            Field
            Message
            Code
          }
          photocaption_2 {
            Field
            Message
            Code
          }
          photourl_3 {
            Field
            Message
            Code
          }
          photocaption_3 {
            Field
            Message
            Code
          }
          photourl_4 {
            Field
            Message
            Code
          }
          photocaption_4 {
            Field
            Message
            Code
          }
          photourl_5 {
            Field
            Message
            Code
          }
          photocaption_5 {
            Field
            Message
            Code
          }
          photourl_6 {
            Field
            Message
            Code
          }
          photocaption_6 {
            Field
            Message
            Code
          }
          hostedauthentication_authenticationemail {
            Field
            Message
            Code
          }
          hostedauthentication_rememberme {
            Field
            Message
            Code
          }
        }
        FieldErrorsOrder
      }
      HasErrors
      Review {
        IsRecommended
        Rating
        ReviewText
        SendEmailAlertWhenCommented
        SendEmailAlertWhenPublished
        SubmissionTime
        Title
      }
    }
  }
`;

export const postReview = async ({
  activity,
  allowGiveaway,
  bodyType,
  callbackUrl,
  client,
  deviceFingerprint,
  email,
  isIncentivizedReview,
  isSweatCollective,
  locale,
  nickname,
  photos,
  productNameId,
  rating,
  ratingFit,
  sendEmailAlertWhenCommented,
  sendEmailAlertWhenPublished,
  text,
  title,
  whatIsYourUsualSize,
  whatSizeDidYouPurchase,
}) => {
  return bazaarvoiceMutation({
    client,
    mutation: postReviewMutation,
    variables: {
      activity,
      allowGiveaway,
      bodyType,
      callbackUrl,
      deviceFingerprint,
      email,
      isIncentivizedReview,
      isSweatCollective,
      locale,
      nickname,
      photos,
      productNameId,
      rating,
      ratingFit,
      sendEmailAlertWhenCommented,
      sendEmailAlertWhenPublished,
      text,
      title,
      whatIsYourUsualSize,
      whatSizeDidYouPurchase,
    },
  });
};

export const getReviewForm = async ({ client, productNameId, user }) => {
  return bazaarvoiceQuery({
    client,
    query: getReviewFormQuery,
    variables: {
      productNameId,
      user,
    },
  });
};

const postCommentMutation = `
  mutation PostReviewComment(
    $locale: String!,
    $commentText: String!,
    $reviewId: String!,
    $hostedAuthenticationCallbackUrl: String,
    $userEmail: String,
    $userNickname: String,
    $user: String
  ) {
    postReviewComment(
      locale: $locale,
      commentText: $commentText,
      reviewId: $reviewId,
      hostedAuthenticationCallbackUrl: $hostedAuthenticationCallbackUrl,
      userEmail: $userEmail,
      userNickname: $userNickname,
      user: $user
    ) {
      Comment {
        SubmissionTime
        CommentText
        SendEmailAlertWhenPublished
      }
      Errors {
        Message
        Code
      }
      SubmissionId
      AuthorSubmissionToken
      HasErrors
    }
  }
`;

export const postComment = async ({
  client,
  locale,
  commentText,
  reviewId,
  hostedAuthenticationCallbackUrl,
  userEmail,
  userNickname,
  user,
}) => {
  return bazaarvoiceMutation({
    client,
    mutation: postCommentMutation,
    variables: {
      locale,
      commentText,
      reviewId,
      hostedAuthenticationCallbackUrl,
      userEmail,
      userNickname,
      user,
    },
  });
};
